"use client"
import { Button, Typography } from "@mui/material"
import {
  DisplayNameModel,
  PdfFileModel,
  ServicePlanModel,
  ServicePlansResponse,
} from "api"
import { useIsDesktop } from "common/MediaQueries"
import NextLink from "common/bit/NextLink"
import FormikForm from "common/bit/form/FormikForm"
import Image from "next/image"
import { useState } from "react"
import FormSelect from "reusable/formsComp/FormSelect"
import styled from "styled-components"
import InnerPageWrapper from "../../../reusable/wrappers/InnerPageWrapper"
import schemaCarFleet, {
  initialFormStatesCarFleet,
} from "../carFleet/SchemaCarFleet"

const StyledInnerPageWrapper = styled(InnerPageWrapper)`
  min-height: 1100px;
`

const StyledHeadlineText = styled(Typography)`
  font-size: 18px;
  margin-bottom: 20px;
  text-align: right;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 40px;
    text-align: center;
  }
`

const StyledFormSelectWrapper = styled.div`
  width: 680px;
  max-width: 100%;
  margin: 0px auto;
  display: flex;
  justify-content: center;
  gap: 40px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    flex-direction: column;
    gap: 0px;
  }
`
const StyledBtnWrapp = styled.div`
  display: flex;
  margin-top: 20px;
  justify-content: center;
  ${(p) => p.theme.breakpoints.down("sm")} {
    justify-content: flex-start;
    margin-top: 25px;
  }
`
const StyledBtn = styled(Button)`
  background: ${(p) => p.theme.colors.headerColor};
  color: ${(p) => p.theme.colors.white};
  width: 296px;
  height: 45px;
  transition: 200ms all ease;
  border: none;
  :hover {
    background-color: ${(p) => p.theme.colors.white};
    color: ${(p) => p.theme.colors.darkBlack};
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 100%;
  }
`

const StyledResultsWrapper = styled.div`
  width: 680px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
  margin: 40px 0 0;
`

const StyledNextLink = styled(NextLink)`
  display: flex;
  gap: 8px;
  align-items: center;
`
const StyledFileName = styled(Typography)`
  text-decoration: underline;
  word-break: break-all;
  font-size: 16px;
`
const StyledDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(p) => p.theme.colors.darkGrey};
  margin-top: 0;
  margin-bottom: 40px;
  opacity: 0.5;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 20px;
  }
`
const StyledDisclaimerWrapper = styled.div`
  margin-top: 60px;
  width: 680px;
  max-width: 100%;
  margin-bottom: 40px;
`

const StyledHeadline = styled(Typography).attrs({
  variant: "h2",
})`
  text-align: right;
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
  }
`
const StyledPhoneLink = styled.a`
  color: ${(p) => p.theme.colors.saphire};
  unicode-bidi: plaintext;
  font-size: 16px;
  display: block;
  padding: 5px 0;
  text-decoration: underline;
  text-align: right;
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
  }
`

const StyledDisclaimer = styled(Typography)`
  font-size: 16px;
  margin-top: 20px;
  text-align: right;
  font-weight: 300;
  ${(p) => p.theme.breakpoints.up("md")} {
    text-align: center;
  }
`

type ServicePlansPageProps = {
  data: ServicePlansResponse
}

const ServicePlansPage = ({ data }: ServicePlansPageProps) => {
  const [selectedModel, setSelectedModel] = useState<ServicePlanModel | null>(
    null
  )
  const [selectedDisplayName, setSelectedDisplayName] =
    useState<DisplayNameModel | null>(null)
  const [pdf, setPdf] = useState<PdfFileModel | null>(null)

  const isDesktop = useIsDesktop()

  const modelsList = data.modelList.sort((a, b) =>
    a.modelName.localeCompare(b.modelName)
  )

  const updateDate = data.lastUpdateDate
    ? new Date(data.lastUpdateDate).toLocaleDateString("en-GB")
    : ""

  return (
    <StyledInnerPageWrapper
      title="תוכנית טיפול מותאמת לרכב שלך"
      subtitle="תוכנית הטיפול מציגה את רשימת החלקים שיש להחליף במעמד טיפול לרכבך.<br/>
הטיפול נדרש לביצוע באחד המקרים הבאים, המוקדם מבניהם: כשמופיעה התראה בלוח המחוונים של רכבך / לאחר נסיעה של 15,000 ק״מ / במידה וחלפה שנה ממועד הטיפול הקודם."
      imgUrl={
        isDesktop
          ? "/img/cover/tutorialvideos.jpg"
          : "/img/cover/tutorialvideosmobile.jpg"
      }
      breadCrumbName="ServicePlans"
    >
      <StyledDivider />
      <StyledHeadlineText>
        להצגת תוכנית הטיפול, יש לבחור את דגם הרכב ושנת העלייה לכביש (מידע זה
        נמצא ברישיון הרכב).
      </StyledHeadlineText>
      <FormikForm
        validationSchema={schemaCarFleet}
        initialValues={initialFormStatesCarFleet}
        onSubmit={async () => {
          if (!selectedDisplayName) return
          setPdf(selectedDisplayName.pdf)
        }}
        render={() => {
          return (
            <>
              <StyledFormSelectWrapper>
                <FormSelect
                  id="modelSelect"
                  name="select"
                  color="primary"
                  label="דגם הרכב"
                  values={modelsList.map((item) => item.modelName)}
                  map={(item) => ({ id: item, name: item })}
                  onChange={(e) => {
                    setSelectedModel(
                      modelsList.find(
                        (m) => m.modelName === e?.target?.value
                      ) ?? null
                    )
                  }}
                  // focused
                  value={selectedModel?.modelName}
                />
                <FormSelect
                  id="DisplayNameSelect"
                  name="displaySelect"
                  color="primary"
                  label="שנת עלייה לכביש"
                  values={[
                    ...new Set(
                      selectedModel?.displayList?.map(
                        (item) => item.displayName
                      )
                    ),
                  ]}
                  map={(item) => ({
                    id: item,
                    name: item,
                  })}
                  onChange={(e) => {
                    setSelectedDisplayName(
                      selectedModel?.displayList.find(
                        (m) => m.displayName === e?.target?.value
                      ) ?? null
                    )
                  }}
                  disabled={!selectedModel}
                  value={selectedDisplayName?.displayName}
                />
              </StyledFormSelectWrapper>
              <StyledBtnWrapp>
                <StyledBtn
                  disabled={!selectedDisplayName}
                  onClick={() => {
                    if (!selectedDisplayName) return
                    setPdf(selectedDisplayName.pdf)
                  }}
                >
                  להצגת תוכנית הטיפול שלי
                </StyledBtn>
              </StyledBtnWrapp>
            </>
          )
        }}
      />
      {pdf && (
        <StyledResultsWrapper>
          <Typography variant="h3" component="h2">
            תוצאות חיפוש:
          </Typography>
          <StyledNextLink openInNewTab href={pdf.url}>
            <Image
              src="/img/filePdf.svg"
              width={32}
              height={32}
              alt=""
              style={{
                maxWidth: "100%",
                height: "auto",
              }}
            />
            <StyledFileName>{pdf.fileName}.pdf</StyledFileName>
          </StyledNextLink>
        </StyledResultsWrapper>
      )}
      <StyledDisclaimerWrapper>
        <StyledHeadline>
          שירות הלקוחות שלנו זמין בשבילך לכל עניין:
        </StyledHeadline>
        <StyledPhoneLink href="tel:*2880">*2880</StyledPhoneLink>
        <StyledDisclaimer>
          המידע המוצג נכון לתאריך {updateDate} וייתכנו בו שינויים מעת לעת בהתאם
          להנחיית היצרן, תכנית הטיפולים עשויה להיות מושפעת ממאפייני הנהיגה, תנאי
          הדרך, האקלים, תחזוקת הרכב, קריאת מפתח הרכב ועוד. יתכן ויידרשו פעולות
          נוספות בהתאם לתוצאות הבדיקה ומצב הרכב
        </StyledDisclaimer>
      </StyledDisclaimerWrapper>
    </StyledInnerPageWrapper>
  )
}

export default ServicePlansPage
